import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { OrganizationStatus } from 'reducers/organizations/OrganizationState';
import {
    updateFilters,
    fetchKpiSummaryThunk,
    fetchSalesAttributionThunk,
    fetchAdjustmentsThunk,
    fetchLeaderboardThunk
} from 'actions/salesDashboard/SalesDashboardActions';
import { getDatesForYearFilter, getDatesForComparisonFilter } from '../utils/DashboardDateUtils';
import { DEFAULT_FILTERS } from '../constants/DashboardData';
import { LeaderboardSortBy } from 'model/salesDashboard';
import { useAuth } from 'hooks/useAuth';
import moment from 'moment-timezone';
import { getComparisonOptionsForYear } from '../utils/DashboardDateUtils';

interface SalesDashboardContextProps {
    UIFilters: {
        year: string;
        comparison: string;
        businessType: string;
        region: string;
        location: string;
    };
    filterActions: {
        setYear: (value: string) => void;
        setComparison: (value: string) => void;
        setBusinessType: (value: string) => void;
        setRegion: (value: string) => void;
        setLocation: (value: string) => void;
    };
}

const SalesDashboardContext = createContext<SalesDashboardContextProps | undefined>(undefined);

export const SalesDashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const auth = useAuth();

    // Attempt to get an organization ID.
    // Usually auth.user.organizationId or we can pull from organizations state if multiple
    const { organizations, status: organizationsStatus } = useSelector((state: RootState) => state.organizations);

    // Fetch organizations if not loaded
    useEffect(() => {
        if (organizationsStatus === OrganizationStatus.Initial) {
            import('actions/organization/OrganizationActions').then(({ fetchOrganizationsThunk }) => {
                dispatch(fetchOrganizationsThunk() as any);
            });
        }
    }, [dispatch, organizationsStatus]);

    const getOrganizationId = (): number | undefined => {
        // Priority 1: Find parent organization
        if (organizations && organizations.length > 0) {
            const parentOrg = organizations.find(org => org.parentOrganizationId == null);
            if (parentOrg) return parentOrg.id;
            return organizations[0].id; // Fallback to first if no explicit parent found
        }

        // Priority 2: Use auth user org id
        if (auth?.user && (auth.user as any).organizationId) {
            return (auth.user as any).organizationId as number;
        }

        return undefined;
    };

    const organizationId = getOrganizationId();
    const filters = useSelector((state: RootState) => state.salesDashboard.filters);

    // Keep track of the UI string representations
    const [selectedYear, setSelectedYear] = useState<string>(DEFAULT_FILTERS.year);
    const [selectedComparison, setSelectedComparison] = useState<string>(DEFAULT_FILTERS.comparison);
    const [selectedBusinessType, setSelectedBusinessType] = useState<string>(DEFAULT_FILTERS.businessType);
    const [selectedRegion, setSelectedRegion] = useState<string>(DEFAULT_FILTERS.region);
    const [selectedLocation, setSelectedLocation] = useState<string>(DEFAULT_FILTERS.location);

    // Trigger API calls when filters change — each fires independently so components
    // render as soon as their own response arrives.
    useEffect(() => {
        if (!organizationId || !filters) return;

        dispatch(fetchKpiSummaryThunk(organizationId, filters) as any);
        dispatch(fetchSalesAttributionThunk(organizationId, filters) as any);
        dispatch(fetchAdjustmentsThunk(organizationId, filters) as any);
        dispatch(fetchLeaderboardThunk(organizationId, { ...filters, sortBy: LeaderboardSortBy.NetSales }) as any);
    }, [organizationId, filters]);

    // Actions
    const handleSetYear = (yearVal: string) => {
        setSelectedYear(yearVal);

        // Ensure comparison is valid for the new year filter
        const dynamicComparisonOptions = getComparisonOptionsForYear(yearVal);
        const validOptionIds = dynamicComparisonOptions.map(opt => opt.id);

        let newComparisonVal = selectedComparison;
        if (!validOptionIds.includes(selectedComparison) && validOptionIds.length > 0) {
            newComparisonVal = validOptionIds[0];
            setSelectedComparison(newComparisonVal);
        }

        const { startDate, endDate } = getDatesForYearFilter(yearVal);
        const { compareStartDate, compareEndDate } = getDatesForComparisonFilter(newComparisonVal, startDate, endDate);

        dispatch(
            updateFilters({
                startDate,
                endDate,
                compareStartDate,
                compareEndDate
            })
        );
    };

    const handleSetComparison = (comparisonVal: string) => {
        setSelectedComparison(comparisonVal);
        const { startDate, endDate } = getDatesForYearFilter(selectedYear);
        const { compareStartDate, compareEndDate } = getDatesForComparisonFilter(comparisonVal, startDate, endDate);

        dispatch(
            updateFilters({
                compareStartDate,
                compareEndDate
            })
        );
    };

    const handleSetBusinessType = (val: string) => {
        setSelectedBusinessType(val);
        dispatch(
            updateFilters({
                businessTypes: val === 'all' ? undefined : [val]
            })
        );
    };

    const handleSetRegion = (val: string) => {
        setSelectedRegion(val);
        // Region might be ignored currently by backend but we persist to UI
    };

    const handleSetLocation = (val: string) => {
        setSelectedLocation(val);
        // Assuming location val is an ID string like "123", or 'all'.
        const numVal = parseInt(val, 10);
        dispatch(
            updateFilters({
                marketplaces: val === 'all' || isNaN(numVal) ? undefined : [numVal]
            })
        );
    };

    const contextValue = useMemo(
        () => ({
            UIFilters: {
                year: selectedYear,
                comparison: selectedComparison,
                businessType: selectedBusinessType,
                region: selectedRegion,
                location: selectedLocation
            },
            filterActions: {
                setYear: handleSetYear,
                setComparison: handleSetComparison,
                setBusinessType: handleSetBusinessType,
                setRegion: handleSetRegion,
                setLocation: handleSetLocation
            }
        }),
        [selectedYear, selectedComparison, selectedBusinessType, selectedRegion, selectedLocation]
    );

    return <SalesDashboardContext.Provider value={contextValue}>{children}</SalesDashboardContext.Provider>;
};

export const useSalesDashboardContext = () => {
    const context = useContext(SalesDashboardContext);
    if (!context) {
        throw new Error('useSalesDashboardContext must be used within a SalesDashboardProvider');
    }
    return context;
};
